import React, { useState, Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowLeftIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import RichTextEditor from '../../components/RichTextEditor';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { trackIncidentOperation } from '../../utils/appInsights';
import { useEngagementResources } from '../../hooks/useEngagementResources';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';

interface FormData {
  shortDescription: string;
  description: string;
  priority: string;
  impact: string;
  urgency: string;
  state: string;
  ticketType: string;
  contactId: string;
  assignedToId: string;
  assignmentGroupId: string;
  customerId: string;
  engagementId: string;
  servicePlanId: string;
  resourceId: string;
  otherResourceNames: string;
  channel: string;
  category: string;
}

const IncidentCreate: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('incidents', 'create');
  const { formatDateTime } = useDateFormatter();
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();

  // Priority calculation function (matches backend logic)
  const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
    if (!impact || !urgency) return 'low_4'; // Default to Low

    const impactNum = parseInt(impact.split('_')[1]);
    const urgencyNum = parseInt(urgency.split('_')[1]);

    if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
    if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
    if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
    if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
    if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

    return 'low_4'; // Default to Low
  };

  const [formData, setFormData] = useState<FormData>({
    shortDescription: '',
    description: '',
    priority: 'low_4',
    impact: 'low_3',
    urgency: 'low_3',
    state: 'new',
    ticketType: 'INCIDENT',
    contactId: '',
    assignedToId: '',
    assignmentGroupId: '',
    customerId: '',
    engagementId: '',
    servicePlanId: '',
    resourceId: '',
    otherResourceNames: '',
    channel: 'web',
    category: 'issue'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);
  const [pinnedResource, setPinnedResource] = useState<{
    id: string;
    name: string;
    resourceId?: string;
  } | null>(null);
  const [contactQuery, setContactQuery] = useState('');
  const [impactQuery, setImpactQuery] = useState('');
  const [urgencyQuery, setUrgencyQuery] = useState('');
  const [channelQuery, setChannelQuery] = useState('');
  const [categoryQuery, setCategoryQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');

  // Dropdown options
  const impactOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const urgencyOptions = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const priorityOptions = [
    { value: 'critical_1', label: '1 - Critical' },
    { value: 'high_2', label: '2 - High' },
    { value: 'moderate_3', label: '3 - Moderate' },
    { value: 'low_4', label: '4 - Low' },
    { value: 'planning_5', label: '5 - Planning' }
  ];

  const channelOptions = [
    { value: 'web', label: 'Web' },
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'alert', label: 'Alert' }
  ];

  const categoryOptions = [
    { value: 'question', label: 'Question' },
    { value: 'issue', label: 'Issue' },
    { value: 'request', label: 'Request' },
    { value: 'change', label: 'Change' }
  ];


  // Fetch contacts for the selected customer - only when customer is selected
  const { data: contactsData, isLoading: isLoadingContacts } = useQuery({
    queryKey: ['customer-contacts', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return { users: [], total: 0 };
      const response = await api.get(`/users?customerId=${formData.customerId}&class=contact&limit=1000`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch assignment groups for dropdown
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      try {
        const response = await api.get('/groups/assignment-groups');
        console.log('Assignment groups response:', response.data);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching assignment groups:', error);
        return [];
      }
    }
  });

  // Fetch group members for assignment dropdown - only when assignment group is selected
  const { data: groupMembersData, isLoading: isLoadingMembers, error: membersError } = useQuery({
    queryKey: ['group-members', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      if (!formData.assignmentGroupId) return [];
      console.log('Fetching members for group:', formData.assignmentGroupId);
      try {
        const members = await fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
        console.log('Group members response:', members);
        return members || [];
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements for dropdown - only when customer is selected
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return { engagements: [] };
      const response = await api.get(`/engagements?customerId=${formData.customerId}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch primary contact for auto-fill when customer/engagement is selected
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Auto-fill contact when customer/engagement is selected and primary contact is found
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  // Fetch service plans for dropdown - only when engagement is selected
  const { data: servicePlansData, isLoading: isLoadingServicePlans } = useQuery({
    queryKey: ['servicePlans', formData.engagementId],
    queryFn: async () => {
      if (!formData.engagementId) return { plans: [], total: 0 };
      console.log('Fetching service plans for engagement:', formData.engagementId);
      try {
        // Get service plans specifically for this engagement
        const response = await api.get(`/engagements/${formData.engagementId}/service-plans`);
        console.log('Service plans response:', response.data);
        return response.data || { plans: [], total: 0 };
      } catch (error) {
        console.error('Error fetching service plans:', error);
        return { plans: [], total: 0 };
      }
    },
    enabled: !!formData.engagementId
  });

  // Fetch resources for dropdown - ServiceNow CIs when engagement is SN-configured
  const { data: resourcesData } = useEngagementResources(
    formData.engagementId,
    resourceQuery,
    pinnedResource
  );
  const isServiceNowResourceSource = resourcesData?.source === 'servicenow';
  const resourceOptions = resourcesData?.resources || [];

  const createIncidentMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.post('/cases', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Invalidate list queries so they refetch when user navigates back
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['incidents'] }),
        queryClient.invalidateQueries({ queryKey: ['cases'] }),
        queryClient.invalidateQueries({ queryKey: ['incident-stats'] })
      ]);
      
      // Log incident creation
      if (user) {
        trackIncidentOperation(
          'Incident Created',
          user.email || user.firstName || 'Unknown',
          {
            action: 'New Incident is successfully created',
            incidentNumber: data.number || data.id || 'Unknown',
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      navigate(`/incidents/${data.id || data._id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create incident';
      setErrors({ submit: message });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const newErrors: Record<string, string> = {};
    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short description is required';
    }
    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      newErrors.engagementId = 'Engagement is required';
    }
    // Resource validation: either select a resource OR enter custom resource names
    if (!formData.resourceId && !isOtherResource) {
      if (!formData.engagementId) {
        newErrors.resourceId = 'Please select an engagement first to choose a resource';
      } else {
        newErrors.resourceId = 'Resource is required (select a resource or choose "Others")';
      }
    }
    if (isOtherResource && !formData.otherResourceNames.trim()) {
      newErrors.otherResourceNames = 'Please enter at least one custom resource name';
    }
    if (!formData.assignmentGroupId) {
      newErrors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clean up empty fields - calculate priority from impact/urgency
    const calculatedPriority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
    const submitData: any = {
      shortDescription: formData.shortDescription,
      priority: calculatedPriority,
      state: formData.state,
      ticketType: 'INCIDENT',
      caseType: 'incident',
      channel: formData.channel,
      category: formData.category,
      impact: formData.impact,
      urgency: formData.urgency
    };

    if (formData.description) submitData.description = formData.description;
    if (formData.contactId) submitData.contactId = formData.contactId;
    if (formData.assignedToId) submitData.assignedToId = formData.assignedToId;
    if (formData.assignmentGroupId) submitData.assignmentGroupId = formData.assignmentGroupId;
    if (formData.customerId) submitData.customerId = formData.customerId;
    if (formData.engagementId) submitData.engagementId = formData.engagementId;
    if (formData.servicePlanId) submitData.servicePlanId = formData.servicePlanId;
    // Handle resource: either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      submitData.otherResourceNames = formData.otherResourceNames.trim();
    } else if (formData.resourceId) {
      submitData.resourceId = formData.resourceId;
    }
    
    createIncidentMutation.mutate(submitData);
  };


  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, description: value }));
    // Clear error for description field
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }));
    }
  };

  const handleShortDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, shortDescription: value }));
    // Clear error for shortDescription field
    if (errors.shortDescription) {
      setErrors(prev => ({ ...prev, shortDescription: '' }));
    }
  };

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to create incidents.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/incidents')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Incidents
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Create New Incident</h1>
          </div>

        <form onSubmit={handleSubmit} className="p-8">
          {errors.submit && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          )}

          {/* Row 1: Number and Opened */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number
                  </label>
                </div>
                <div className="col-span-9">
                  <input
                    type="text"
                    value=""
                    readOnly
                    className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                  />
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opened
                  </label>
                </div>
                <div className="col-span-9">
                  <input
                    type="text"
                    value={formatDateTime(new Date())}
                    readOnly
                    className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Impact and Urgency */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="impact" className="block text-sm font-medium text-gray-700 mb-1">
                    Impact
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.impact}
                    onChange={(value) => {
                      const newImpact = value || 'low_3';
                      const newPriority = calculatePriorityFromImpactUrgency(newImpact, formData.urgency);
                      setFormData(prev => ({ ...prev, impact: newImpact, priority: newPriority }));
                    }}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        displayValue={(value: string) => {
                          const option = impactOptions.find(o => o.value === value);
                          return option ? option.label : '';
                        }}
                        onChange={(e) => setImpactQuery(e.target.value)}
                        placeholder="Select Impact"
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setImpactQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {impactOptions
                            .filter(option => option.label.toLowerCase().includes(impactQuery.toLowerCase()))
                            .map((option) => (
                              <Combobox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {option.label}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                    Urgency
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.urgency}
                    onChange={(value) => {
                      const newUrgency = value || 'low_3';
                      const newPriority = calculatePriorityFromImpactUrgency(formData.impact, newUrgency);
                      setFormData(prev => ({ ...prev, urgency: newUrgency, priority: newPriority }));
                    }}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        displayValue={(value: string) => {
                          const option = urgencyOptions.find(o => o.value === value);
                          return option ? option.label : '';
                        }}
                        onChange={(e) => setUrgencyQuery(e.target.value)}
                        placeholder="Select Urgency"
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setUrgencyQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {urgencyOptions
                            .filter(option => option.label.toLowerCase().includes(urgencyQuery.toLowerCase()))
                            .map((option) => (
                              <Combobox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {option.label}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Priority and State */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                </div>
                <div className="col-span-9">
                  <input
                    type="text"
                    id="priority"
                    value={priorityOptions.find(o => o.value === formData.priority)?.label || ''}
                    readOnly
                    className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                  />
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                </div>
                <div className="col-span-9">
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value="New"
                    readOnly
                    className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Row 4: Channel and Category */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="channel" className="block text-sm font-medium text-gray-700 mb-1">
                    Channel
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.channel}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, channel: value || 'web' }));
                    }}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        displayValue={(value: string) => {
                          const option = channelOptions.find(o => o.value === value);
                          return option ? option.label : '';
                        }}
                        onChange={(e) => setChannelQuery(e.target.value)}
                        placeholder="Select Channel"
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setChannelQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {channelOptions
                            .filter(option => option.label.toLowerCase().includes(channelQuery.toLowerCase()))
                            .map((option) => (
                              <Combobox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {option.label}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.category}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, category: value || 'issue' }));
                    }}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        displayValue={(value: string) => {
                          const option = categoryOptions.find(o => o.value === value);
                          return option ? option.label : '';
                        }}
                        onChange={(e) => setCategoryQuery(e.target.value)}
                        placeholder="Select Category"
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setCategoryQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {categoryOptions
                            .filter(option => option.label.toLowerCase().includes(categoryQuery.toLowerCase()))
                            .map((option) => (
                              <Combobox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {option.label}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                </div>
              </div>
            </div>
          </div>

          {/* Row 5: Customer and Engagement */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">
                    Customer <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.customerId}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        customerId: value || '',
                        engagementId: '',
                        servicePlanId: '',
                        resourceId: '',
                        otherResourceNames: '',
                        contactId: ''
                      }));
                      setSelectedResourceIdValue('');
                      setIsOtherResource(false);
                      setPinnedResource(null);
                      if (errors.customerId) {
                        setErrors(prev => ({ ...prev, customerId: '' }));
                      }
                    }}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                          errors.customerId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                        }`}
                        displayValue={(customerId: string) => {
                          const customer = (customersData?.customers || []).find((c: any) => c.id === customerId);
                          return customer ? customer.accountName : '';
                        }}
                        onChange={(e) => setCustomerQuery(e.target.value)}
                        placeholder="Select Customer"
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setCustomerQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {(customersData?.customers || [])
                            .filter((customer: any) => customer && customer.id && customer.accountName && customer.accountName.toLowerCase().includes(customerQuery.toLowerCase()))
                            .map((customer: any) => (
                              <Combobox.Option
                                key={customer.id}
                                value={customer.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {customer.accountName}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                  {errors.customerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="engagementId" className="block text-sm font-medium text-gray-700 mb-1">
                    Engagement <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.engagementId}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        engagementId: value || '',
                        servicePlanId: '',
                        resourceId: '',
                        otherResourceNames: '',
                        contactId: ''
                      }));
                      setSelectedResourceIdValue('');
                      setIsOtherResource(false);
                      setPinnedResource(null);
                      if (errors.engagementId) {
                        setErrors(prev => ({ ...prev, engagementId: '' }));
                      }
                    }}
                    disabled={!formData.customerId}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                          errors.engagementId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                        }`}
                        displayValue={(engagementId: string) => {
                          const engagement = (engagementsData?.engagements || []).find((e: any) => e.id === engagementId);
                          return engagement ? engagement.name : '';
                        }}
                        onChange={(e) => setEngagementQuery(e.target.value)}
                        placeholder={formData.customerId ? "Select Engagement" : "Select Customer first"}
                        disabled={!formData.customerId}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" disabled={!formData.customerId}>
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setEngagementQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {(engagementsData?.engagements || [])
                            .filter((engagement: any) => engagement && engagement.id && engagement.name && engagement.name.toLowerCase().includes(engagementQuery.toLowerCase()))
                            .map((engagement: any) => (
                              <Combobox.Option
                                key={engagement.id}
                                value={engagement.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {engagement.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                  {errors.engagementId && (
                    <p className="mt-1 text-sm text-red-600">{errors.engagementId}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Row 6: Contact and Service Plan */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="contactId" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.contactId}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, contactId: value || '' }));
                    }}
                    disabled={!formData.engagementId}
                  >
                    <div className="relative">
                      <div className="relative w-full">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 pr-10 disabled:bg-gray-50 disabled:text-gray-400"
                          displayValue={(userId: string) => {
                            const user = (contactsData?.users || []).find((u: any) => u.id === userId);
                            return user ? `${user.firstName} ${user.lastName}` : '';
                          }}
                          onChange={(event) => setContactQuery(event.target.value)}
                          placeholder={formData.engagementId ? "Search contact by name or email..." : "Select engagement first"}
                          disabled={!formData.engagementId}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" disabled={!formData.engagementId}>
                          <ChevronUpDownIcon
                            className="h-4 w-4 text-gray-400"
                            aria-hidden="true"
                          />
                        </Combobox.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setContactQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {(() => {
                            if (isLoadingContacts) {
                              return (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  Loading contacts...
                                </div>
                              );
                            }

                            const filteredContacts = (contactsData?.users || []).filter((user: any) => {
                              if (!user || !user.id || !user.firstName) return false;
                              const searchStr = `${user.firstName} ${user.lastName} ${user.email || ''}`.toLowerCase();
                              return searchStr.includes(contactQuery.toLowerCase());
                            });

                            if (filteredContacts.length === 0) {
                              return (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  No contacts found for this engagement.
                                </div>
                              );
                            }

                            return filteredContacts.map((user: any) => (
                              <Combobox.Option
                                key={user.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                                value={user.id}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {user.firstName} {user.lastName}
                                      {user.email && (
                                        <span className={`ml-2 text-xs ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                                          ({user.email})
                                        </span>
                                      )}
                                    </span>
                                    {selected ? (
                                      <span
                                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-indigo-600'
                                        }`}
                                      >
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Combobox.Option>
                            ));
                          })()}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="servicePlanId" className="block text-sm font-medium text-gray-700 mb-1">
                    Service Plan
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.servicePlanId}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, servicePlanId: value || '' }));
                    }}
                    disabled={!formData.engagementId || isLoadingServicePlans}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                        displayValue={(servicePlanId: string) => {
                          const plan = (servicePlansData?.plans || []).find((p: any) => p.id === servicePlanId);
                          return plan ? plan.name : '';
                        }}
                        onChange={(e) => setServicePlanQuery(e.target.value)}
                        placeholder={isLoadingServicePlans ? 'Loading service plans...' : (formData.engagementId ? 'Select Service Plan' : 'Select Engagement first')}
                        disabled={!formData.engagementId || isLoadingServicePlans}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" disabled={!formData.engagementId || isLoadingServicePlans}>
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setServicePlanQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {(servicePlansData?.plans || [])
                            .filter((plan: any) => plan && plan.id && plan.name && plan.name.toLowerCase().includes(servicePlanQuery.toLowerCase()))
                            .map((plan: any) => (
                              <Combobox.Option
                                key={plan.id}
                                value={plan.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {plan.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                </div>
              </div>
            </div>
          </div>

          {/* Row 7: Resource and Resource ID / Custom Resources */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="resourceId" className="block text-sm font-medium text-gray-700 mb-1">
                    Resource <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={isOtherResource ? '__OTHER__' : formData.resourceId}
                    onChange={(value) => {
                      if (value === '__OTHER__') {
                        // User selected "Others"
                        setIsOtherResource(true);
                        setPinnedResource(null);
                        setFormData(prev => ({ ...prev, resourceId: '', otherResourceNames: '' }));
                        setSelectedResourceIdValue('');
                      } else {
                        // User selected a normal resource
                        setIsOtherResource(false);
                        const selectedResource = resourceOptions.find((r: any) => r.id === value);
                        setPinnedResource(
                          selectedResource
                            ? {
                                id: selectedResource.id,
                                name: selectedResource.name,
                                resourceId: selectedResource.resourceId,
                              }
                            : null
                        );
                        setFormData(prev => ({ ...prev, resourceId: value || '', otherResourceNames: '' }));
                        setSelectedResourceIdValue(selectedResource?.resourceId || '');
                      }
                      if (errors.resourceId) {
                        setErrors(prev => ({ ...prev, resourceId: '' }));
                      }
                      if (errors.otherResourceNames) {
                        setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                      }
                    }}
                    disabled={!formData.engagementId}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                          errors.resourceId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                        }`}
                        displayValue={(resourceId: string) => {
                          if (isOtherResource || resourceId === '__OTHER__') return 'Others (specify custom resources)';
                          const resource = resourceOptions.find((r: any) => r.id === resourceId);
                          return resource?.name || pinnedResource?.name || '';
                        }}
                        onChange={(e) => setResourceQuery(e.target.value)}
                        placeholder={
                          !formData.engagementId
                            ? 'Select Engagement first'
                            : isServiceNowResourceSource
                              ? 'Select Configuration Item'
                              : 'Select Resource'
                        }
                        disabled={!formData.engagementId}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" disabled={!formData.engagementId}>
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setResourceQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {resourceOptions
                            .filter((resource: any) => {
                              if (!resource?.id || !resource?.name) return false;
                              // ServiceNow search already runs against full CMDB server-side
                              if (isServiceNowResourceSource) return true;
                              return resource.name.toLowerCase().includes(resourceQuery.toLowerCase());
                            })
                            .map((resource: any) => (
                              <Combobox.Option
                                key={resource.id}
                                value={resource.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {resource.name}
                                    </span>
                                    {selected && !isOtherResource && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          {/* Others option - always shown at the end */}
                          <Combobox.Option
                            key="__OTHER__"
                            value="__OTHER__"
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-10 pr-4 border-t border-gray-200 ${
                                active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                              }`
                            }
                          >
                            {({ selected, active }) => (
                              <>
                                <span className={`block truncate ${selected || isOtherResource ? 'font-medium' : 'font-normal'}`}>
                                  Others (specify custom resources)
                                </span>
                                {isOtherResource && (
                                  <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? 'text-white' : 'text-indigo-600'
                                  }`}>
                                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Combobox.Option>
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                  {errors.resourceId && (
                    <p className="mt-1 text-sm text-red-600">{errors.resourceId}</p>
                  )}
                </div>
              </div>
            </div>
            {/* Conditional: Show either Resource ID (read-only) OR Custom Resources (editable) */}
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isOtherResource ? (
                      <>Custom Resources <span className="text-red-500">*</span></>
                    ) : (
                      'Resource ID'
                    )}
                  </label>
                </div>
                <div className="col-span-9">
                  {isOtherResource ? (
                    <input
                      type="text"
                      value={formData.otherResourceNames}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, otherResourceNames: e.target.value }));
                        if (errors.otherResourceNames) {
                          setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                        }
                      }}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.otherResourceNames ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                      }`}
                      placeholder="Enter resource names separated by commas"
                    />
                  ) : (
                    <input
                      type="text"
                      value={selectedResourceIdValue}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 truncate"
                      placeholder="Select a resource to view Resource ID"
                      title={selectedResourceIdValue}
                    />
                  )}
                </div>
              </div>
              {/* Helper text and error below the grid to maintain alignment */}
              {isOtherResource && (
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3"></div>
                  <div className="col-span-9">
                    <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                    {errors.otherResourceNames && (
                      <p className="mt-1 text-sm text-red-600">{errors.otherResourceNames}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Row 8: Assignment Group and Assigned To */}
          <div className="grid grid-cols-12 gap-6 mb-6">
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label htmlFor="assignmentGroupId" className="block text-sm font-medium text-gray-700 mb-1">
                    Assignment Group <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.assignmentGroupId}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        assignmentGroupId: value || '',
                        assignedToId: ''
                      }));
                      if (errors.assignmentGroupId) {
                        setErrors(prev => ({ ...prev, assignmentGroupId: '' }));
                      }
                    }}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                          errors.assignmentGroupId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                        }`}
                        displayValue={(groupId: string) => {
                          const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                          return group ? group.name : '';
                        }}
                        onChange={(e) => setAssignmentGroupQuery(e.target.value)}
                        placeholder="Select Assignment Group"
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setAssignmentGroupQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {(assignmentGroupsData || [])
                            .filter((group: any) => group && group.id && group.name && group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                            .map((group: any) => (
                              <Combobox.Option
                                key={group.id}
                                value={group.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {group.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                  {errors.assignmentGroupId && (
                    <p className="mt-1 text-sm text-red-600">{errors.assignmentGroupId}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-6">
              <div className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned To
                  </label>
                </div>
                <div className="col-span-9">
                  <Combobox
                    value={formData.assignedToId}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, assignedToId: value || '' }));
                    }}
                    disabled={!formData.assignmentGroupId || isLoadingMembers}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                          errors.assignedToId ? 'border-red-300 focus:ring-red-400' : 'border-gray-300'
                        }`}
                        displayValue={(memberId: string) => {
                          const member = (groupMembersData || []).find((m: any) => m.id === memberId);
                          return member ? `${member.firstName} ${member.lastName}` : '';
                        }}
                        onChange={(e) => setAssignedToQuery(e.target.value)}
                        placeholder={isLoadingMembers ? 'Loading members...' : (formData.assignmentGroupId ? 'Select Assignee' : 'Select Assignment Group first')}
                        disabled={!formData.assignmentGroupId || isLoadingMembers}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" disabled={!formData.assignmentGroupId || isLoadingMembers}>
                        <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                      </Combobox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setAssignedToQuery('')}
                      >
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {(groupMembersData || [])
                            .filter((member: any) => {
                              if (!member || !member.id || !member.firstName) return false;
                              const searchStr = `${member.firstName} ${member.lastName}`.toLowerCase();
                              return searchStr.includes(assignedToQuery.toLowerCase());
                            })
                            .map((member: any) => (
                              <Combobox.Option
                                key={member.id}
                                value={member.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {member.firstName} {member.lastName}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-indigo-600'
                                      }`}>
                                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                  {errors.assignedToId && (
                    <p className="mt-1 text-sm text-red-600">{errors.assignedToId}</p>
                  )}
                  {membersError && (
                    <p className="mt-1 text-sm text-red-600">Error loading group members</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 mb-6">
            {/* Short Description - Full width */}
            <div className="col-span-12">
              <div className="grid grid-cols-12 gap-4 items-start">
                <div className="col-span-2">
                  <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                    <span className="text-red-500">*</span> Short Description
                  </label>
                </div>
                <div className="col-span-10">
                  <RichTextEditor
                    value={formData.shortDescription}
                    onChange={handleShortDescriptionChange}
                    placeholder="Brief summary of the incident"
                    minHeight="80px"
                  />
                  {errors.shortDescription && (
                    <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 mb-8">
            {/* Description - Full width */}
            <div className="col-span-12">
              <div className="grid grid-cols-12 gap-4 items-start">
                <div className="col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                    Description
                  </label>
                </div>
                <div className="col-span-10">
                  <RichTextEditor
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    placeholder="Enter incident description"
                    minHeight="120px"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/incidents')}
              className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createIncidentMutation.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createIncidentMutation.isPending ? 'Creating...' : 'Create Incident'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default IncidentCreate; 